import { Operation, Tag, TagCategory } from './types'
import { round } from './calc'

export type Reliability = 'Muy baja' | 'Baja' | 'Media' | 'Alta' | 'Muy alta'

export function reliabilityOf(count: number): Reliability {
  if (count >= 50) return 'Muy alta'
  if (count >= 20) return 'Alta'
  if (count >= 10) return 'Media'
  if (count >= 5) return 'Baja'
  return 'Muy baja'
}

export interface ConfluenceStats {
  key: string
  label: string
  count: number
  wins: number
  losses: number
  breakeven: number
  winRate: number
  resultPct: number
  resultMoney: number
  avgPct: number
  avgR: number
  profitFactor: number | null
  maxDrawdown: number
  avgWinPct: number
  avgLossPct: number
  bestPct: number
  worstPct: number
  winStreak: number
  lossStreak: number
  planFollowRate: number
  reliability: Reliability
  score: number
}

function sortKey(op: Operation): number {
  return new Date(`${op.date}T${op.entryTime || '00:00'}:00`).getTime()
}

export function statsFor(key: string, label: string, ops: Operation[]): ConfluenceStats {
  const sorted = [...ops].sort((a, b) => sortKey(a) - sortKey(b))
  const count = sorted.length

  let wins = 0
  let losses = 0
  let breakeven = 0
  let resultPct = 0
  let resultMoney = 0
  let sumR = 0
  let grossWin = 0
  let grossLoss = 0
  let winTotal = 0
  let lossTotal = 0
  let peak = 0
  let cum = 0
  let maxDrawdown = 0
  let winStreak = 0
  let lossStreak = 0
  let currentWin = 0
  let currentLoss = 0
  let planFollowed = 0
  let planKnown = 0
  let bestPct = count ? -Infinity : 0
  let worstPct = count ? Infinity : 0

  for (const op of sorted) {
    resultPct += op.resultPct
    resultMoney += op.resultMoney ?? 0
    sumR += op.resultR

    if (op.resultPct > 0) {
      wins += 1
      grossWin += op.resultPct
      winTotal += op.resultPct
      currentWin += 1
      currentLoss = 0
    } else if (op.resultPct < 0) {
      losses += 1
      grossLoss += Math.abs(op.resultPct)
      lossTotal += Math.abs(op.resultPct)
      currentLoss += 1
      currentWin = 0
    } else {
      breakeven += 1
      currentWin = 0
      currentLoss = 0
    }
    winStreak = Math.max(winStreak, currentWin)
    lossStreak = Math.max(lossStreak, currentLoss)

    if (op.followedPlan === true || op.followedPlan === false) {
      planKnown += 1
      if (op.followedPlan) planFollowed += 1
    }

    cum += op.resultPct
    if (cum > peak) peak = cum
    maxDrawdown = Math.max(maxDrawdown, peak - cum)

    bestPct = Math.max(bestPct, op.resultPct)
    worstPct = Math.min(worstPct, op.resultPct)
  }

  const decided = wins + losses
  const winRate = decided ? (wins / decided) * 100 : 0
  const profitFactor = grossLoss > 0 ? grossWin / grossLoss : null
  const avgPct = count ? resultPct / count : 0
  const avgR = count ? sumR / count : 0

  // Composite score: quality first, weighted by how much evidence backs it up.
  const pfComponent = profitFactor === null ? (grossWin > 0 ? 1.5 : 0) : Math.min(profitFactor, 4) - 1
  const quality = (winRate - 50) / 50 * 22 + pfComponent * 12 + avgPct * 9 + avgR * 6
  const evidence = 0.55 + 0.45 * Math.min(count, 40) / 40
  const score = count ? round(quality * evidence, 2) : 0

  return {
    key,
    label,
    count,
    wins,
    losses,
    breakeven,
    winRate: round(winRate, 1),
    resultPct: round(resultPct),
    resultMoney: round(resultMoney),
    avgPct: round(avgPct),
    avgR: round(avgR, 2),
    profitFactor: profitFactor === null ? null : round(profitFactor, 2),
    maxDrawdown: round(maxDrawdown),
    avgWinPct: wins ? round(winTotal / wins) : 0,
    avgLossPct: losses ? round(-lossTotal / losses) : 0,
    bestPct: count ? round(bestPct) : 0,
    worstPct: count ? round(worstPct) : 0,
    winStreak,
    lossStreak,
    planFollowRate: planKnown ? round((planFollowed / planKnown) * 100, 1) : 0,
    reliability: reliabilityOf(count),
    score
  }
}

function tagsOfCategory(op: Operation, categoryId: string, tagById: Map<string, Tag>): Tag[] {
  return op.tagIds
    .map((id) => tagById.get(id))
    .filter((tag): tag is Tag => !!tag && tag.categoryId === categoryId)
}

/** Per-tag stats inside one category. Tag ids are the identity, labels are just display. */
export function statsByCategory(
  ops: Operation[],
  category: TagCategory,
  tags: Tag[]
): ConfluenceStats[] {
  const categoryTags = tags.filter((tag) => tag.categoryId === category.id)
  return categoryTags
    .map((tag) => statsFor(tag.id, tag.label, ops.filter((op) => op.tagIds.includes(tag.id))))
    .filter((stats) => stats.count > 0)
    .sort((a, b) => b.score - a.score)
}

interface ComboKey {
  key: string
  label: string
}

function comboKeyFor(op: Operation, categories: TagCategory[], tagById: Map<string, Tag>): ComboKey | null {
  const parts: string[] = []
  const ids: string[] = []
  for (const category of categories) {
    const selected = tagsOfCategory(op, category.id, tagById).sort((a, b) => a.order - b.order)
    if (!selected.length) return null
    parts.push(selected.map((tag) => tag.label).join(' + '))
    ids.push(selected.map((tag) => tag.id).join('|'))
  }
  return { key: ids.join('::'), label: parts.join(' + ') }
}

/** Full or partial combinations across the given categories (ids are identity). */
export function statsByCombination(
  ops: Operation[],
  categories: TagCategory[],
  tags: Tag[]
): ConfluenceStats[] {
  if (!categories.length) return []
  const tagById = new Map(tags.map((tag) => [tag.id, tag]))
  const groups = new Map<string, { label: string; ops: Operation[] }>()

  for (const op of ops) {
    const combo = comboKeyFor(op, categories, tagById)
    if (!combo) continue
    const group = groups.get(combo.key) ?? { label: combo.label, ops: [] }
    group.ops.push(op)
    groups.set(combo.key, group)
  }

  return Array.from(groups.entries())
    .map(([key, group]) => statsFor(key, group.label, group.ops))
    .sort((a, b) => b.score - a.score)
}

export interface PlanImpact {
  followed: ConfluenceStats
  broken: ConfluenceStats
  unknown: number
}

export function planImpact(ops: Operation[]): PlanImpact {
  return {
    followed: statsFor('followed', 'Siguiendo el plan', ops.filter((op) => op.followedPlan === true)),
    broken: statsFor('broken', 'Fuera del plan', ops.filter((op) => op.followedPlan === false)),
    unknown: ops.filter((op) => op.followedPlan === null || op.followedPlan === undefined).length
  }
}

/** Best / worst entries honouring the minimum sample size. */
export function rankBySample(stats: ConfluenceStats[], minSample: number) {
  const eligible = stats.filter((item) => item.count >= minSample)
  const insufficient = stats.filter((item) => item.count < minSample)
  return {
    best: [...eligible].sort((a, b) => b.score - a.score),
    worst: [...eligible].sort((a, b) => a.score - b.score),
    insufficient
  }
}
